// plan-03 S05 / plan-10 S04: canonical command registry — bible §19

import { bump, load, set } from '../core/save'
import { play } from '../audio/sfx'
import { emit } from '../core/bus'
import { streamText, createStreamTarget } from './streaming'
import { UNKNOWN_LINE } from './pool'
import { handleDivine } from '../widgets/divine'
import { handleConsensus, handleDecode, handlePaint } from '../widgets/heatmap'
import { handleDirectoryText } from '../widgets/directory'
import { triggerEngage } from '../widgets/portal'
import type { CursorController } from './cursor'

export interface CommandCtx {
  history: HTMLElement
  cursor: CursorController
  streamFn: typeof streamText
  abortSignal?: AbortSignal
}

export type CommandHandler = (ctx: CommandCtx, args: string[]) => Promise<void>

async function streamResponse(ctx: CommandCtx, text: string, cls?: string): Promise<void> {
  const target = createStreamTarget(ctx.history, cls ?? 'vt-line-oracle')
  ctx.cursor.attach(target)
  await ctx.streamFn(target, text, { signal: ctx.abortSignal })
  ctx.cursor.detach()
}

async function streamLines(ctx: CommandCtx, lines: string[], cls?: string): Promise<void> {
  for (const line of lines) {
    await streamResponse(ctx, line, cls)
  }
}

const registry = new Map<string, CommandHandler>()

// help — updated for plan-10
const HELP_LINES = [
  'commands:',
  '  help          — this list.',
  '  whoami        — visitor identity.',
  '  48            — 48.',
  '  divine [hash] — generate vaguepost.',
  '  consensus <w> — run heatmap.',
  '  decode        — brute-force decode.',
  '  paint <key>   — recall paint pattern.',
  '  directory     — toggle consensus directory.',
  '  directory --text — inline directory dump.',
  '  engage        — portal to vague souls.',
  '  nodes         — consensus network.',
  '  tape          — play the "probably" mixtape.',
  '  vague [mirror] — coronation apparatus.',
  '  tune          — tune in to broadcast.',
  '  replay [date] — replay broadcast.',
  '  clear         — clear terminal buffer.',
  '  mute / unmute — audio toggle.',
  '  legacy        — archival surface.',
  '',
  'shell commands:',
  '  ls [path]     — list directory.',
  '  cd [path]     — change directory.',
  '  cat <file>    — read a file.',
  '  pwd           — print working directory.',
  '  play <file>   — open video player.',
  '  ..            — go up one level.',
  '  ~             — return to root.',
  '  tree [path]   — list directory (alias for ls).',
  '',
  'commune:',
  '  commune [q]   — ask the oracle. deterministically.',
  '  ask [q]       — alias for commune.',
  '  consultations — review past questions.',
  '  oracle [q]    — consult the live oracle. it does not answer.',
  '  scry [q]      — alias for oracle.',
]

registry.set('help', async (ctx) => {
  await streamLines(ctx, HELP_LINES)
})

// whoami
registry.set('whoami', async (ctx) => {
  const s = load()
  const { ensureVisitorAlias } = await import('../widgets/network/roster')
  const alias = ensureVisitorAlias()
  await streamResponse(ctx, `node ${alias}. handle: visitor. events: ${s.events}. status: probably.`)
})

// 48
registry.set('48', async (ctx) => {
  await streamResponse(ctx, '48.')
  play('command_accept')
  setTimeout(() => play('command_accept'), 200)
  bump('events')
})

// divine — plan 04 + plan-10: streams post + activates stage
registry.set('divine', async (ctx, args) => {
  emit('oracle.command', { cmd: 'divine', args })
  await handleDivine(ctx, args)
  // plan-13 S08: network echo — ghost node also rolls a hash
  import('../widgets/network/ticker').then(({ injectEcho }) => {
    injectEcho('divine', 'hash echo')
  }).catch(() => {})
})

// consensus — plan 05 + plan-10: activates stage heatmap
registry.set('consensus', async (ctx, args) => {
  emit('oracle.command', { cmd: 'consensus', args })
  await handleConsensus(ctx, args)
  // plan-13 S08: network echo — ghost node also runs consensus
  import('../widgets/network/ticker').then(({ injectEcho }) => {
    const similarWords = ['drift', 'king', 'node', 'signal', 'quorum', 'gateway', 'relay', 'cursor']
    const word = args[0] || similarWords[Math.floor(Math.random() * similarWords.length)]
    injectEcho('consensus', word)
  }).catch(() => {})
})

// decode — plan 05 + plan-10: requires heatmap on stage
registry.set('decode', async (ctx) => {
  emit('oracle.command', { cmd: 'decode', args: [] })
  await handleDecode(ctx)
})

// paint — plan 05
registry.set('paint', async (ctx, args) => {
  emit('oracle.command', { cmd: 'paint', args })
  await handlePaint(ctx, args)
})

// tables — placeholder for plan 07
registry.set('tables', async (ctx, args) => {
  const n = parseInt(args[0] ?? '1', 10) || 1
  emit('oracle.command', { cmd: 'tables', args: [String(n)] })
  await streamResponse(ctx, `> table ${n} loading. probably.`)
})

// log — placeholder for plan 07
registry.set('log', async (ctx) => {
  emit('oracle.command', { cmd: 'log', args: [] })
  await streamResponse(ctx, '> log pending. probably.')
})

// directory — plan-06 + plan-10: toggles stage; --text is legacy dump
registry.set('directory', async (ctx, args) => {
  emit('oracle.command', { cmd: 'directory', args })

  if (args[0] === '--text') {
    // legacy inline dump
    await handleDirectoryText(ctx)
    return
  }

  // toggle directory in stage
  const { getStage } = await import('../widgets/stage/stage')
  const stage = getStage()
  if (stage) {
    const isActive = stage.isActive('directory')
    stage.toggle('directory')
    await streamResponse(ctx, isActive ? '> directory: closed.' : '> directory: stage.')
  }
  play('command_accept')
})

// engage — plan-08 + plan-10: stage portal card, then descend on second engage
registry.set('engage', async (ctx) => {
  emit('oracle.command', { cmd: 'engage', args: [] })

  const { getStage } = await import('../widgets/stage/stage')
  const stage = getStage()

  if (stage?.isActive('portal')) {
    // portal card is already on stage — trigger full descent
    await streamResponse(ctx, '> portal opening. proximity: probably.')
    triggerEngage()
  } else {
    // activate portal card on stage
    stage?.activate('portal')
    await streamResponse(ctx, '> portal stage active. engage to descend.')
  }
})

// clear — plan-10: clears terminal buffer only; stage is unaffected
registry.set('clear', async (ctx) => {
  ctx.history.innerHTML = ''
})

// mute
registry.set('mute', async (ctx) => {
  const s = load()
  if (!s.audioMuted) {
    set('audioMuted', true)
    await streamResponse(ctx, '> muted.')
  } else {
    await streamResponse(ctx, '> already muted.')
  }
})

// unmute
registry.set('unmute', async (ctx) => {
  const s = load()
  if (s.audioMuted) {
    set('audioMuted', false)
    await streamResponse(ctx, '> unmuted.')
  } else {
    await streamResponse(ctx, '> already unmuted.')
  }
})

// palimpsest — plan 09
registry.set('palimpsest', async (ctx) => {
  emit('oracle.command', { cmd: 'palimpsest', args: [] })
  const { triggerType2 } = await import('../eggs/palimpsest')
  triggerType2()
  const { triggerMourning } = await import('../eggs/silenceRitual')
  await triggerMourning(ctx)
})

// 49 — plan 09
registry.set('49', async (ctx) => {
  const { fire } = await import('../eggs/pixel49')
  fire('command')
  await streamResponse(ctx, '49.')
})

// legacy — plan 09
registry.set('legacy', async (ctx) => {
  emit('oracle.command', { cmd: 'legacy', args: [] })
  const { handleLegacy } = await import('../eggs/palimpsest')
  await handleLegacy(ctx)
})

// tape — toggle the probably mixtape player on stage. aliases: mixtape, probably
registry.set('tape', async (ctx) => {
  emit('oracle.command', { cmd: 'tape', args: [] })
  const { getStage } = await import('../widgets/stage/stage')
  const stage = getStage()
  if (stage) {
    const isActive = stage.isActive('tape')
    stage.toggle('tape')
    await streamResponse(ctx, isActive ? '> tape: ejected.' : '> tape: loaded. probably. press play.')
  }
  play('command_accept')
})

registry.set('mixtape', async (ctx, args) => {
  const handler = registry.get('tape')!
  await handler(ctx, args)
})

registry.set('probably', async (ctx, args) => {
  const handler = registry.get('tape')!
  await handler(ctx, args)
})

// vague — plan-20 S04: the coronation apparatus (vague-ifier). no args activates
// the widget; arg `mirror` activates AND begins the camera flow — the command IS
// the user gesture (the widget never requests the camera on mount or any other
// path; the doctrine analog of PART IX #10 no-autoplay).
registry.set('vague', async (ctx, args) => {
  emit('oracle.command', { cmd: 'vague', args })
  const { getStage } = await import('../widgets/stage/stage')
  const stage = getStage()
  const mirror = args[0] === 'mirror'
  stage?.activate('vagueifier', mirror ? ['mirror'] : [])
  await streamResponse(ctx, mirror ? '> the mirror opens. probably.' : '> vague-ifier: stage.')
  // plan-13 S08 network echo — a ghost node also coronates
  import('../widgets/network/ticker').then(({ injectEcho }) => {
    injectEcho('vague', 'coronation')
  }).catch(() => {})
})

// nodes — plan-13 S04: toggle consensus network panel on stage
registry.set('nodes', async (ctx) => {
  emit('oracle.command', { cmd: 'nodes', args: [] })
  const { getStage } = await import('../widgets/stage/stage')
  const stage = getStage()
  if (stage) {
    const isActive = stage.isActive('nodes')
    stage.toggle('nodes')
    await streamResponse(ctx, isActive ? '> network: closed.' : '> network: stage.')
  }
  play('command_accept')
})

// tune / broadcast — plan-13 S06: activate broadcast widget
registry.set('tune', async (ctx) => {
  emit('oracle.command', { cmd: 'tune', args: [] })
  const { broadcastDate } = await import('../core/seed')
  const timing = broadcastDate()
  if (timing.isBroadcastWindow) {
    const { getStage } = await import('../widgets/stage/stage')
    const stage = getStage()
    stage?.activate('broadcast')
    await streamResponse(ctx, '> broadcast: tuning in.')
  } else {
    const totalSec = Math.floor(timing.msUntilNext / 1000)
    const hh = String(Math.floor(totalSec / 3600)).padStart(2, '0')
    const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0')
    const ss = String(totalSec % 60).padStart(2, '0')
    await streamResponse(ctx, `> next broadcast in ${hh}:${mm}:${ss}.`)
  }
  play('command_accept')
})

registry.set('broadcast', async (ctx, args) => {
  emit('oracle.command', { cmd: 'broadcast', args })
  // alias for tune
  const handler = registry.get('tune')!
  await handler(ctx, args)
})

// replay — plan-13 S06: replay broadcast
registry.set('replay', async (ctx, args) => {
  emit('oracle.command', { cmd: 'replay', args })
  const { utcDate } = await import('../core/seed')
  const todayKey = utcDate()

  if (args[0]) {
    const replayDate = args[0]
    // validate format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(replayDate)) {
      await streamResponse(ctx, '> invalid date format. use: replay YYYY-MM-DD.')
      play('command_reject')
      return
    }
    // check 7-day window
    const replayMs = new Date(replayDate + 'T00:00:00Z').getTime()
    const todayMs = new Date(todayKey + 'T00:00:00Z').getTime()
    const dayDiff = (todayMs - replayMs) / 86400000
    if (dayDiff > 7 || dayDiff < 0) {
      await streamResponse(ctx, '> archive purged. consensus: probably.')
      return
    }
    const { getStage } = await import('../widgets/stage/stage')
    const stage = getStage()
    stage?.activate('broadcast', ['replay', replayDate])
    await streamResponse(ctx, `> replaying broadcast ${replayDate}.`)
  } else {
    // replay today
    const { getStage } = await import('../widgets/stage/stage')
    const stage = getStage()
    stage?.activate('broadcast', ['replay', todayKey])
    await streamResponse(ctx, `> replaying today's broadcast.`)
  }
  play('command_accept')
})

// commune — plan-18 S05: deterministic Q&A oracle
registry.set('commune', async (ctx, args) => {
  emit('oracle.command', { cmd: 'commune', args })
  const { getStage } = await import('../widgets/stage/stage')
  const stage = getStage()

  if (args.length === 0) {
    // no arg: activate widget with empty input
    stage?.activate('commune')
    await streamResponse(ctx, '> commune: stage.')
    play('command_accept')
    return
  }

  // hash form from URL: hash:XXXXX
  if (args[0]?.startsWith('hash:')) {
    const hash = args[0].slice(5)
    const s = load()
    const log = s.communeLog || []
    const entry = log.find(e => e.qHash === hash)

    // increment shared seen counter
    if (s.communeSharedSeen !== undefined) {
      s.communeSharedSeen++
      const { save: saveSave } = await import('../core/save')
      saveSave(s)
    }

    if (entry) {
      stage?.activate('commune', args)
      await streamResponse(ctx, '> commune: replayed from share.')
    } else {
      stage?.activate('commune', args)
      await streamResponse(ctx, '> commune: hash received. question unknown.')
    }
    play('command_accept')
    return
  }

  // inline question: activate widget AND submit
  const question = args.join(' ')
  stage?.activate('commune', [question])
  await streamResponse(ctx, '> commune: stage.')
  play('command_accept')
})

// ask — plan-18 S05: alias for commune
registry.set('ask', async (ctx, args) => {
  emit('oracle.command', { cmd: 'commune', args })
  const handler = registry.get('commune')!
  await handler(ctx, args)
})

// consultations — plan-18 S05: list saved Q&A pairs inline
registry.set('consultations', async (ctx) => {
  emit('oracle.command', { cmd: 'consultations', args: [] })
  const s = load()
  const log = s.communeLog || []
  if (log.length === 0) {
    await streamResponse(ctx, '> consultations: empty. ask something first.')
    play('command_accept')
    return
  }
  const recent = log.slice(-12)
  for (const entry of recent) {
    const date = new Date(entry.at)
    const ts = date.toISOString().slice(0, 16).replace('T', ' ')
    await streamResponse(ctx, `> ${ts} · ${entry.q} · @${entry.qHash.slice(0, 4)}.`)
  }
  play('command_accept')
})

// oracle — plan-31 / §46: the live non-deterministic oracle. NO network echo
// (it is non-deterministic + stateless; an echo would be a lie). The command is
// the gesture: no arg opens the widget idle; a question summons (no fetch on mount).
registry.set('oracle', async (ctx, args) => {
  emit('oracle.command', { cmd: 'oracle', args })
  const { getStage } = await import('../widgets/stage/stage')
  const stage = getStage()
  stage?.activate('oracle', args)
  await streamResponse(ctx, args.length ? '> oracle: summoned.' : '> oracle: stage.')
  play('command_accept')
})

// scry — alias for oracle
registry.set('scry', async (ctx, args) => {
  emit('oracle.command', { cmd: 'oracle', args })
  const handler = registry.get('oracle')!
  await handler(ctx, args)
})

export function getCommand(cmd: string): CommandHandler | undefined {
  return registry.get(cmd)
}

export function hasCommand(cmd: string): boolean {
  return registry.has(cmd)
}

export { UNKNOWN_LINE }
